import { setIsLoading } from '../../Lib/redux/slices/navigation';
import {
  setBlockedDestinationPage,
  setPendingMoveForwards,
  setShouldNavigateBackward,
} from '../../Lib/redux/slices/navigationControls';
import { updateAndExposeDynamicHeightData } from '../../Utils/utils';
import { updateHasStatementGraduallyDisappeared } from '../../Lib/redux/slices/legalStatement';

export const handleClickBackSideEffects = ({
  dispatch,
  callBackFunction,
  isDynamicHeightEnabled,
  isConsentStatementAboveNavigation,
  isPenultimatePage,
  hasStatementGraduallyDisappeared,
  minimumWidgetHeight,
  maximumWidgetHeight,
  statement,
}) => {
  const { blockNavigation } =
    window?.__private__?.dynamicWidgetHeightData || {};
  if (
    isDynamicHeightEnabled &&
    !blockNavigation &&
    isConsentStatementAboveNavigation &&
    isPenultimatePage &&
    !hasStatementGraduallyDisappeared &&
    statement
  ) {
    handleLegalStatementDisappearance(
      dispatch,
      minimumWidgetHeight,
      maximumWidgetHeight,
      callBackFunction
    );
  } else {
    callBackFunction();
  }
};

/** handleLegalStatementDisappearance
 *  Why this helper cannot live in the <LegalStatement />
 *  This logic triggers multiple CSS transition animations. To prevent these
 *  from getting cut short or get skipped altogether due to re-rendering (which
 *  always takes precedence over animations), this function needs to be called
 *  as a side-effect of an idle UI, i.e. on BACK CTA clicks.
 */
const handleLegalStatementDisappearance = (
  dispatch,
  minimumWidgetHeight,
  maximumWidgetHeight,
  onCallBack
) => {
  const legalStatementContainer = document.querySelector(
    "[data-testid='legalStatementContainer']"
  );
  const consentStatementSpan = document.getElementById('consentStatementSpan');
  const widget = document.body;
  const widgetHeight = widget?.getBoundingClientRect()?.height;
  const isDynamicHeightExceedingMaximumHeight =
    widget && typeof widgetHeight === 'number' && !Number.isNaN(widgetHeight)
      ? widgetHeight >= maximumWidgetHeight
      : false;
  const inputAreaDomQueryString = isDynamicHeightExceedingMaximumHeight
    ? "[data-testid='inputAreaContainer']"
    : '#inputAreaParentContainer';
  const inputAreaParentContainer =
    document.querySelector(
      inputAreaDomQueryString
      /*
       * This fallback OR case is necessary because the browser zoom level can cause
       * the widget height to drop ~0.5 pixels below the maximumWidgetheight figure,
       * causing the isDynamicHeightExceedingMaximumHeight value to become false
       * incorrectly when the ScrollContainer has not actually vanished.
       * To replicate this set the page zoom to 67% on chrome on this form
       * https://chameleondevs.github.io/a1-staging/index13.html
       * and then try to navigate back after reaching the submission page.
       * BACK navigation will not work if the OR case below is removed.
       */
    ) || document.querySelector("[data-testid='inputAreaContainer']");
  const innerFooterContainer = document.querySelector('#innerFooterContainer');
  const footerSectionHeight =
    innerFooterContainer?.getBoundingClientRect().height;
  const { headerRef } = window?.__private__?.dynamicWidgetHeightData || {};
  const headerSectionHeight =
    headerRef?.current?.getBoundingClientRect().height;

  if (!consentStatementSpan || !inputAreaParentContainer) {
    return;
  }

  const consentStatementHeight =
    consentStatementSpan.parentElement.getBoundingClientRect().height;
  const consentStatementMargin = getComputedStyle(
    consentStatementSpan.parentElement.parentElement,
    null
  )?.marginBottom;
  const consentStatementHeightWithMargin =
    consentStatementHeight + parseInt(consentStatementMargin, 10);
  const futureFooterSectionHeight =
    footerSectionHeight - consentStatementHeightWithMargin;

  consentStatementSpan.parentElement.style.transition =
    'font-size 0.6s ease 0s';
  legalStatementContainer.style.transition = 'margin-bottom 0.6s ease 0s';
  const currentInputAreaHeight =
    inputAreaParentContainer.getBoundingClientRect().height;

  const widgetHeightIsAtItsMinimum =
    document.body.getBoundingClientRect().height === minimumWidgetHeight;
  const heightDeficitAfterLegalStatementUnmounting =
    minimumWidgetHeight -
    (headerSectionHeight + currentInputAreaHeight + futureFooterSectionHeight);
  const widgetElementsWillFallBelowHeightMinimum =
    heightDeficitAfterLegalStatementUnmounting >= 0;

  if (
    !widgetHeightIsAtItsMinimum &&
    !widgetElementsWillFallBelowHeightMinimum
  ) {
    const updateStateAndTriggerCallback = () => {
      dispatch(
        updateHasStatementGraduallyDisappeared({
          hasStatementGraduallyDisappeared: true,
        })
      );
      onCallBack();
      consentStatementSpan.parentElement.removeEventListener(
        'transitionend',
        updateStateAndTriggerCallback
      );
    };
    consentStatementSpan.parentElement.addEventListener(
      'transitionend',
      updateStateAndTriggerCallback
    );
    updateAndExposeDynamicHeightData({
      requiredInputAreaHeight: undefined,
      shouldResizeInputArea: false,
    });
    inputAreaParentContainer.style.maxHeight = `${currentInputAreaHeight}px`;
    inputAreaParentContainer.style.minHeight = `${currentInputAreaHeight}px`;
  } else {
    /*  This logic prevents a bad UX in cases where the widget height on this
     *  page (final form page, aka penultimate page) is at the minimumWidgetHeight
     *  and the subsequently disappearing consent statement leads to the remaining
     *  page content dipping below the minimumWidgetHeight. It is achieved by
     *  allowing the input area to grow and fully absorb the newly available
     *  widget space.
     */
    consentStatementSpan.parentElement.style.transition = '';
    legalStatementContainer.style.transition = '';

    // The following two commands ensure the acceptable UX explained above
    inputAreaParentContainer.style.maxHeight = 'none';
    inputAreaParentContainer.style.minHeight = `${currentInputAreaHeight}px`;
  }

  // The following three commands kick off the gradual shrinking and disappearance of the consent statement
  consentStatementSpan.style.color = 'white';
  legalStatementContainer.style.marginBottom = 0;
  consentStatementSpan.parentElement.style.fontSize = '0px';

  if (widgetHeightIsAtItsMinimum || widgetElementsWillFallBelowHeightMinimum) {
    setTimeout(() => {
      dispatch(
        updateHasStatementGraduallyDisappeared({
          hasStatementGraduallyDisappeared: true,
        })
      );
      onCallBack();
    }, 0);
  }
};

const cleanupOfTransientForwardNavRequest = ({
  pendingMoveForwards,
  blockedDestinationPage,
  isLoading,
  dispatch,
}) => {
  dispatch(setPendingMoveForwards({ isAwaitingAsyncRequest: false }));
  if (pendingMoveForwards) {
    if (blockedDestinationPage)
      dispatch(setBlockedDestinationPage({ pageNumber: null }));
    if (isLoading) {
      dispatch(setIsLoading({ isLoading: false }));
    }
  }
};

export const handleClickBackward = ({
  dispatch,
  isDynamicHeightEnabled,
  isConsentStatementAboveNavigation,
  pendingMoveForwards,
  blockedDestinationPage,
  isLoading,
  isPenultimatePage,
  hasStatementGraduallyDisappeared,
  minimumWidgetHeight,
  maximumWidgetHeight,
  statement,
}) => {
  cleanupOfTransientForwardNavRequest({
    pendingMoveForwards,
    blockedDestinationPage,
    isLoading,
    dispatch,
  });
  handleClickBackSideEffects({
    callBackFunction: () => {
      dispatch(setShouldNavigateBackward({ shouldNavigateBackward: true }));
    },
    dispatch,
    isDynamicHeightEnabled,
    isConsentStatementAboveNavigation,
    isPenultimatePage,
    hasStatementGraduallyDisappeared,
    minimumWidgetHeight,
    maximumWidgetHeight,
    statement,
  });
};

export const getButtonTextForCurrentPage = ({
  currentPage,
  navigationBarButtonTextConfig,
  isFinalPageBeforeSubmission = false,
  isPenultimatePage = false,
}) => {
  const customCtaOverrides =
    navigationBarButtonTextConfig?.customCtaOverrides || {};

  if (isFinalPageBeforeSubmission || isPenultimatePage) {
    return navigationBarButtonTextConfig?.submit || 'Submit';
  }

  const customPageText = customCtaOverrides[currentPage]?.text;
  if (customPageText) {
    return customPageText;
  }

  if (currentPage === 0) {
    return navigationBarButtonTextConfig?.start || 'Start';
  }

  return navigationBarButtonTextConfig?.continue || 'Continue';
};

export const getSubmitButtonText = (
  submissionPageNumber,
  totalPages,
  navigationBarButtonTextConfig
) => {
  const customCtaOverrides =
    navigationBarButtonTextConfig?.customCtaOverrides || {};

  const lastPreSubmissionPageNumber =
    typeof submissionPageNumber === 'number'
      ? submissionPageNumber
      : totalPages - 1;

  const customSubmitText =
    customCtaOverrides[lastPreSubmissionPageNumber]?.text;

  if (customSubmitText) {
    return customSubmitText;
  }

  return navigationBarButtonTextConfig?.submit || 'Submit';
};
